import { useCallback, useEffect, useState } from 'react';
import {
  computeCapJourneyProgress,
  type AcademicLevel,
  type CapCode,
  type CapComplianceReport,
  type CapJourneyProgress,
  type CapProposalStatus,
} from '@dosfilos/domain';
import { capApi } from '../lib/api/cap.api';
import { useTenantStore } from '../stores/tenant.store';

// ─── Per-program CAP journey progress ─────────────────────────────────────────
// Mirrors use-setup-progress (data-driven, no manual checkboxes, TTL cache) but
// scoped to one program + CAP. The 5 steps are derived by the domain from three
// signals the platform already computes: the live audit report, whether a
// snapshot was saved, and the current proposal status.

export interface CapProgress {
  loading: boolean;
  error: boolean;
  progress: CapJourneyProgress | null;
  /** Live audit report (academic signals). null while loading / on error. */
  report: Omit<CapComplianceReport, 'id'> | null;
  /** Most recent saved snapshot, if any (for the audit step's status detail). */
  latestSnapshot: CapComplianceReport | null;
  proposalStatus: CapProposalStatus | null;
  /** Force a re-fetch, bypassing the cache (call after completing a step). */
  refresh: () => void;
}

interface Signals {
  report: Omit<CapComplianceReport, 'id'> | null;
  latestSnapshot: CapComplianceReport | null;
  proposalStatus: CapProposalStatus | null;
}

const CACHE_TTL_MS = 30_000;
const cache = new Map<string, { at: number; signals: Signals }>();

function keyOf(tid: string, programId: string, capCode: CapCode, level: AcademicLevel): string {
  return `${tid}|${programId}|${capCode}|${level}`;
}

async function detect(
  tid: string,
  programId: string,
  capCode: CapCode,
  level: AcademicLevel,
): Promise<Signals> {
  // The academic signals don't depend on non-academic attestations, so run the
  // audit with none — the "audit saved" step reads the saved snapshot instead.
  const [reportRes, snapsRes, propRes] = await Promise.allSettled([
    capApi.runComplianceAudit({
      tenantId: tid,
      programId,
      capCode,
      level,
    }),
    capApi.listComplianceSnapshots({ tenantId: tid, programId, capCode }),
    capApi.getProposal({ tenantId: tid, programId, capCode, level }),
  ]);

  const report = reportRes.status === 'fulfilled' ? reportRes.value : null;
  const snapshots = snapsRes.status === 'fulfilled' ? snapsRes.value.snapshots : [];
  const proposal = propRes.status === 'fulfilled' ? propRes.value.proposal : null;

  return {
    report,
    latestSnapshot: snapshots[0] ?? null,
    proposalStatus: proposal?.status ?? null,
  };
}

/**
 * Live CAP journey progress for one program + CAP. Recomputes the 5-step
 * checklist from data so it stays in sync as the coordinator completes work.
 */
export function useCapProgress(
  programId: string | undefined,
  capCode: CapCode,
  level: AcademicLevel,
): CapProgress {
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const [signals, setSignals] = useState<Signals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    if (!currentTenantId || !programId) {
      setSignals(null);
      setLoading(false);
      return;
    }
    const k = keyOf(currentTenantId, programId, capCode, level);
    const hit = cache.get(k);
    if (nonce === 0 && hit && Date.now() - hit.at < CACHE_TTL_MS) {
      setSignals(hit.signals);
      setLoading(false);
      setError(false);
      return;
    }
    setLoading(true);
    setError(false);
    detect(currentTenantId, programId, capCode, level)
      .then((s) => {
        cache.set(k, { at: Date.now(), signals: s });
        if (!alive) return;
        setSignals(s);
        setError(s.report === null);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [currentTenantId, programId, capCode, level, nonce]);

  const progress = signals
    ? computeCapJourneyProgress({
        report: signals.report,
        hasSavedSnapshot: signals.latestSnapshot !== null,
        ...(signals.latestSnapshot ? { savedStatus: signals.latestSnapshot.overallStatus } : {}),
        proposalStatus: signals.proposalStatus,
      })
    : null;

  return {
    loading,
    error,
    progress,
    report: signals?.report ?? null,
    latestSnapshot: signals?.latestSnapshot ?? null,
    proposalStatus: signals?.proposalStatus ?? null,
    refresh,
  };
}
